'use client';

import { useState, useEffect } from 'react';
import { Building2, Briefcase, Home, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfiniteMarquee } from '@/components/ui/InfiniteMarquee';

// Row 1: Primary property categories
const propertyRow1 = [
  { label: 'Single-Family', icon: '🏠' },
  { label: 'Multi-Family', icon: '🏢' },
  { label: 'Commercial Office', icon: '🏬' },
  { label: 'HOA / Condo', icon: '🏘️' },
  { label: 'Short-Term Rental', icon: '🏡' },
  { label: 'Vacation Rental', icon: '🌴' },
  { label: 'Mixed-Use', icon: '🏗️' },
  { label: 'Healthcare', icon: '🏥' },
  { label: 'Retail', icon: '🛍️' },
  { label: 'Industrial', icon: '🏭' },
  { label: 'Self-Storage', icon: '📦' },
  { label: 'Senior Living', icon: '🏡' },
];

// Row 2: Secondary / specialty types
const propertyRow2 = [
  { label: 'Government', icon: '🏛️' },
  { label: 'Educational', icon: '🎓' },
  { label: 'Hospitality', icon: '🏨' },
  { label: 'Marina', icon: '⛵' },
  { label: 'RV Park', icon: '🚐' },
  { label: 'Golf Course', icon: '⛳' },
  { label: 'Fitness Center', icon: '🏋️' },
  { label: 'Mobile Home', icon: '🏕️' },
  { label: 'Data Center', icon: '🖥️' },
  { label: 'Places of Worship', icon: '⛪' },
  { label: 'Co-Working', icon: '💼' },
  { label: 'Parking Garage', icon: '🅿️' },
];

const audiences = [
  {
    icon: Building2,
    title: 'Property Managers',
    subtitle: '5-100+ units',
    description: 'Manage vendors, track work orders, and get real-time visibility across your entire portfolio — all from one dashboard built for your workflow.',
    painPoint: '"I spend 20+ hours a week coordinating repairs."',
  },
  {
    icon: Briefcase,
    title: 'Commercial Operators',
    subtitle: 'Office, Retail, Industrial',
    description: 'Fast response times keep tenants happy. Automated SLA tracking, preventive maintenance scheduling, and professional reporting at your fingertips.',
    painPoint: '"My tenants complain about slow maintenance."',
  },
  {
    icon: Home,
    title: 'HOA Boards',
    subtitle: 'Communities & Condos',
    description: 'Transparent reporting for board meetings, vendor accountability, and a homeowner portal for service requests — all with zero training required.',
    painPoint: '"We have no visibility into what\'s happening."',
  },
];

const WhoItsFor = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('who-its-for');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="who-its-for" className="relative py-16 sm:py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-gray-100/30 to-gray-50 dark:from-secondary-950 dark:via-secondary-900/30 dark:to-secondary-950" />

      <div className="relative z-10">
        {/* Section Header */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center mb-10">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4",
              "bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            74+ Property Types
          </span>
          <h2
            className={cn(
              "font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-4",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            <span className="text-gray-900 dark:text-secondary-100">One Platform.</span>
            <span className="text-primary-600 dark:text-primary-400"> Any Property.</span>
          </h2>
          <p
            className={cn(
              "max-w-2xl mx-auto text-lg text-gray-500 dark:text-secondary-400",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            Whether you manage 5 properties or 500, Axis Datum scales with you.
          </p>
        </div>

        {/* Property Type Marquee — Full width, edge-to-edge */}
        <div
          className={cn(
            "space-y-3 mb-10 sm:mb-16",
            "transition-all duration-700 ease-out",
            isVisible ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDelay: '300ms' }}
        >
          <InfiniteMarquee items={propertyRow1} duration={25} direction="left" />
          <InfiniteMarquee items={propertyRow2} duration={20} direction="right" />
        </div>

        {/* Audience Cards */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5 sm:gap-8">
            {audiences.map((audience, i) => (
              <div
                key={audience.title}
                className={cn(
                  "group relative",
                  "transition-all duration-700 ease-out",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
                style={{ transitionDelay: `${500 + i * 150}ms` }}
              >
                <div
                  className={cn(
                    "relative p-5 sm:p-6 md:p-8 rounded-2xl h-full flex flex-col",
                    "bg-white shadow-lg dark:shadow-none dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.08]",
                    "hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:border-primary-500/30",
                    "transition-all duration-300"
                  )}
                >
                  {/* Icon & Title */}
                  <div className="flex items-start gap-3 sm:gap-4 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary-500/10 border border-primary-500/20">
                      <audience.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-secondary-100">
                        {audience.title}
                      </h3>
                      <span className="text-sm text-gray-400 dark:text-secondary-500">{audience.subtitle}</span>
                    </div>
                  </div>

                  {/* Pain Point Quote */}
                  <div className="mb-4 p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                    <p className="text-sm text-red-400/80 italic">{audience.painPoint}</p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-500 dark:text-secondary-400 leading-relaxed mb-6 flex-grow">
                    {audience.description}
                  </p>

                  {/* CTA */}
                  <a
                    href="#contact"
                    className={cn(
                      "inline-flex items-center gap-2 text-sm font-medium",
                      "text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300",
                      "transition-colors duration-200"
                    )}
                  >
                    Join the Waitlist
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { WhoItsFor };
